import React from 'react'
import PropTypes from 'prop-types'
// import { saveError } from '../../../api/user';
import CustomBridge from '../../../controllers/bridge/CustomBridge'
import { apm } from '@elastic/apm-rum'
import { isLocalhost } from '../../../utils/is-localhost'
import { isCanceledAxiosError } from '../../../utils/api/apm'
import { METRIC_EVENTS, recordMetricEvent } from '../../../api/metrics'

const STACK_LENGTH = 500
const CRITICAL = 'critical'
const SLIGHT = 'slight'
const GTM_ERROR = 'GTMError'

const CRITICAL_ERRORS = [
  'Out of Memory',
  'Stack Overflow',
  'Maximum call stack exceeded',
  'InternalError',
  'CanvasLostError',
]

const RELOAD_CONFIG = {
  production: CRITICAL_ERRORS,
  development: [],
  // eslint-disable-next-line no-undef
}[process.env.NODE_ENV || 'production']

const CRITICAL_CLASSES = [TypeError, ReferenceError, SyntaxError]

export const RELOAD_REASONS = {
  CRITICAL_ERROR: 'critical_error',
  OFFLINE_RETRY: 'offline_retry',
  GAME_END_NETWORK_ERROR: 'game_end_network_error',
}

// Запасная задержка, если до очереди apm не добрались: обычный таймер очереди
// (flushInterval, 500 м) должен успеть стартовать отправку до reload
const FLUSH_FALLBACK_DELAY = 1000

let hasError = false
let reloadScheduled = false

const performReload = (reason) => {
  if (window.top !== window.self) {
    CustomBridge.instance.send({ name: 'RELOAD', type: 0, data: reason })
  } else {
    window.location.reload()
  }
}

/**
 * apm-rum@5 не имеет публичного flush: очередь уходит на intake по таймеру
 * flushInterval (500 м), и reload раньше таймера убивал запрос на лету —
 * Kibana теряла примерно половину AppReload. Отправляем накопленную очередь
 * вручную и возвращаем promise доставки (сам запрос живёт до собственного
 * таймаута apm — 10 с, дольше reload не задержится).
 */
const flushApmQueue = () => {
  try {
    const apmServer = apm.serviceFactory?.getService?.('ApmServer')
    const { queue } = apmServer || {}
    if (!queue?.items?.length) return Promise.resolve()
    const items = queue.items
    queue._clear()
    return apmServer.sendEvents(items) || Promise.resolve()
  } catch {
    // internals apm-rum могли измениться после апгрейда — тогда рассчитываем
    // только на обычный таймер очереди и запасную задержку
    return new Promise((resolve) => setTimeout(resolve, FLUSH_FALLBACK_DELAY))
  }
}

export const reloadApplication = (reason = 'unknown') => {
  if (reloadScheduled) return
  reloadScheduled = true

  const reloadError = new Error(`Application reload: ${reason}`)
  reloadError.name = 'AppReload'
  apm.captureError(reloadError, {
    labels: {
      'reload.reason': reason,
      'error.uid': getUserUid(),
    },
  })

  // Grafana: общий счётчик + счётчик по причине
  const metricPromises = [recordMetricEvent(METRIC_EVENTS.APP_RELOAD_TOTAL)]
  const reasonMetric = {
    [RELOAD_REASONS.CRITICAL_ERROR]: METRIC_EVENTS.APP_RELOAD_CRITICAL_ERROR,
    [RELOAD_REASONS.OFFLINE_RETRY]: METRIC_EVENTS.APP_RELOAD_OFFLINE_RETRY,
    [RELOAD_REASONS.GAME_END_NETWORK_ERROR]: METRIC_EVENTS.APP_RELOAD_GAME_END_NETWORK_ERROR,
  }[reason]
  if (reasonMetric) {
    metricPromises.push(recordMetricEvent(reasonMetric))
  }

  // Reload только после доставки телеметрии: Kibana (flush очереди apm)
  // и Grafana (ответы POST /metrics/events)
  Promise.all([flushApmQueue().catch(() => {}), ...metricPromises]).finally(() =>
    performReload(reason),
  )
}

const getUserUid = () => {
  try {
    // eslint-disable-next-line no-undef
    return JSON.parse(localStorage?.getItem('info') || '{"uid": "unknown"}')?.uid || 'unknown'
  } catch {
    return 'unknown'
  }
}

const classifyError = (error) => {
  if (!error || error.name === 'AbortError') return { isCritical: false, needsReload: false }

  const { message = '', stack = '' } = error
  const hasCriticalMessage = CRITICAL_ERRORS.some(
    (name) => message?.includes(name) || stack?.includes(name),
  )
  const hasCriticalClass = CRITICAL_CLASSES.some((ErrorClass) => error instanceof ErrorClass)
  const needsReload = RELOAD_CONFIG.some((name) => message?.includes(name) || stack?.includes(name))
  const isCritical = hasCriticalMessage || hasCriticalClass

  return {
    isCritical,
    needsReload,
    type: isCritical ? CRITICAL : SLIGHT,
  }
}

const isGtmError = (error) =>
  error && typeof error.stack === 'string' && error.stack.toLowerCase().includes('gtm')

const isHaveError = () => hasError || isLocalhost()

export const errorHandler = (error) => {
  if (isHaveError()) return
  hasError = true

  const { type } = classifyError(error)

  if (isGtmError(error)) {
    error.name = GTM_ERROR
  }

  apm.captureError(error)
  // const errorData = { type, uid, text };
  // saveError(errorData);

  return type
}

export const apiError = (error) => {
  if (isHaveError() || isCanceledAxiosError(error)) return
  hasError = true

  // const type = SLIGHT;
  // const uid = getUserUid();
  // const text = JSON.stringify(error);
  // const errorData = { type, uid, text };
  // saveError(errorData);

  return error
}

class ErrorBoundary extends React.Component {
  _handleError = (error) => {
    if (!error) return

    const { needsReload } = classifyError(error)
    errorHandler(error)

    if (needsReload) {
      // eslint-disable-next-line no-undef
      setTimeout(() => reloadApplication(RELOAD_REASONS.CRITICAL_ERROR), 2000)
    }
  }

  componentDidCatch(error, errorInfo) {
    this._handleError(error)
  }

  handleWindowError = (event) => {
    const { error } = event
    if (error) {
      this._handleError(error)
    }
  }

  componentDidMount() {
    window?.addEventListener?.('error', this.handleWindowError)
  }

  componentWillUnmount() {
    window?.removeEventListener?.('error', this.handleWindowError)
  }

  render() {
    return this.props.children
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
}

export default ErrorBoundary
